import { Grid } from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import WSButton from "../../../Components/Button/WSButton";
import WSDataGridNew, {
  IColumns,
} from "../../../Components/DataGrid/WSDataGridNew";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { buildImageUrl, updateMainHeader } from "../../../commonFunctions/CommonFunctions";
import config from "../../../Config/config.json";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import http from "../../../commonFunctions/httpClient";

interface ScanCategory {
  id: string;
  name: string;
  imgUrl: string;
}

export const ScanCategoryListing: React.FC<any> = React.memo(() => {
  const navigate = useNavigate();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    updateMainHeader("Scan Category");
    breadCrumbsStore.updateBreadCrumbs([
      { "Scan Category": "/scan-category/list" },
    ]);
  }, []);

  // POST /api/pharmacy/ScanCategory/search
  const fetchCategories = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError("");
      const response: any = await http.post("pharmacy/ScanCategory/search", {});
      const list =
        response?.data || response?.items || response?.categories || response || [];
      const items: ScanCategory[] = Array.isArray(list) ? list : [];

      // The grid's "img" type renders `config.imgUrl + imgUrl`, so store the path
      // relative to config.imgUrl (buildImageUrl resolves the correct absolute URL).
      const transformed = items.map((item) => ({
        ...item,
        imgUrl: item.imgUrl
          ? buildImageUrl(item.imgUrl, config.imgUrl).replace(config.imgUrl, "")
          : "",
      }));
      setData(transformed);
    } catch (err: any) {
      console.error("fetchCategories error:", err);
      setError(err?.message || "Failed to fetch scan categories");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // DELETE /api/pharmacy/ScanCategory/{id}
  const handleDelete = useCallback(
    async (id: string) => {
      if (!id || !window.confirm("Delete this scan category?")) return;
      try {
        await http.del(`pharmacy/ScanCategory/${id}`);
        fetchCategories();
      } catch (err: any) {
        console.error("Delete error:", err);
      }
    },
    [fetchCategories]
  );

  const handleAction = (action: string, row: any) => {
    if (action === "Edit") {
      navigate(`/scan-category/${row?.id}`);
    } else if (action === "Delete") {
      handleDelete(row?.id);
    }
  };

  const handleAdd = () => navigate("/scan-category/add");

  const columns: IColumns[] = [
    { header: "Scan Category Name", fieldName: "name", type: "default" },
    { header: "Image", fieldName: "imgUrl", type: "img" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: ["Edit", "Delete"],
      actionCallback: (action, row) => handleAction(action, row),
    },
  ];

  if (loading) {
    return (
      <Grid container spacing={2} mt={1} sx={{ paddingLeft: 1 }}>
        <Grid
          item
          sm={12}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <div>Loading scan categories...</div>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item sm={12} display="flex" justifyContent="flex-end">
          <WSButton
            id="btnAddScanCategory"
            label="Add Scan Category"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              borderRadius: "999px",
              backgroundColor: "#098963!important",
              textTransform: "none",
              fontWeight: 500,
              height: "35px!important",
              fontSize: "0.875rem!important",
              paddingX: 3,
              paddingY: 1,
              "&:hover": { backgroundColor: "#087654!important" },
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={1} sx={{ paddingLeft: 1 }}>
        <Grid item sm={12}>
          {error ? (
            <div style={{ padding: 20, color: "red" }}>
              {error}. <button onClick={fetchCategories}>Retry</button>
            </div>
          ) : (
            <WSDataGridNew
              columns={columns}
              data={data}
              id="scanCategoryListGrid"
              title={`Scan Categories (${data.length})`}
              exportCsv={true}
              searchBox={{ fieldName: "name" }}
              gridHeaderSx={{
                backgroundColor: "whitesmoke",
                border: "1px solid whitesmoke",
              }}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
});

export default ScanCategoryListing;
